import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { filter, take } from 'rxjs/operators';

const UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
] as const;

export type UtmParams = Partial<Record<(typeof UTM_KEYS)[number], string>>;

/**
 * Captures UTM query parameters from the landing URL on app startup.
 * Injected in AppComponent to ensure params are captured before the user navigates away.
 */
@Injectable({ providedIn: 'root' })
export class UtmService {
    private activatedRoute = inject(ActivatedRoute);

    private readonly queryParams = toSignal(
        this.activatedRoute.queryParams.pipe(
            filter(params => Object.keys(params).length > 0),
            take(1)
        ),
        {
            initialValue: {},
        }
    );

    readonly params = computed<UtmParams>(() => {
        const queryparams = this.queryParams();
        return Object.keys(queryparams)
            .filter(key => UTM_KEYS.includes(key as (typeof UTM_KEYS)[number]))
            .reduce((acc, key) => ({ ...acc, [key]: queryparams[key] }), {});
    });
}
